import React from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

type SectionProps = React.PropsWithChildren<{ title: string }>;
const Section: React.FC<SectionProps> = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
      {title}
    </Typography>
    <Divider sx={{ mb: 2 }} />
    <Box>{children}</Box>
  </Box>
);

export const LeapHelp: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ p: 3, mt: 10, width: '100%' }}>
      <Box sx={{ maxWidth: '900px', mr: 'auto', ml: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Leap Integration Help
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/settings/integrations/leap/setup')}
          >
            Back to Setup
          </Button>
        </Box>

        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Follow the steps below to connect your Leap account to Paiv. If you
          need assistance, contact{' '}
          <Link href="mailto:support@paiv.ai">support@paiv.ai</Link>.
        </Typography>

        <Section title="1. Get your Leap API Key">
          <List dense>
            <ListItem>
              <ListItemText primary="Sign in to your Leap account as an administrator." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Navigate to Settings → Developer → Generate and Copy a Token (Name this token 'Paiv Integration')." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Paste the Token into the 'Leap API Key' field on the Paiv Leap setup page and click 'Fetch Stages'. Match your Leap stages with Paiv stages." />
            </ListItem>
          </List>
        </Section>

        <Section title="2. Map Leap Stages to Paiv Stages">
          <List dense>
            <ListItem>
              <ListItemText primary="On the Paiv Leap setup page, select the corresponding Paiv stage for each Leap workflow stage." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Click 'Save Integration'. All mappings and your API key will be securely stored." />
            </ListItem>
          </List>
        </Section>

        <Section title="3. Add the Paiv Webhook in Leap">
          <List dense>
            <ListItem>
              <ListItemText
                primary="Save your integration in Paiv first. After saving, Paiv will show your unique Webhook URL. Copy this URL.
 "
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="In Leap, go to Settings → Webhooks → Add a new Webhook URL → Save" />
            </ListItem>
          </List>
        </Section>

        <Section title="Troubleshooting">
          <List dense>
            <ListItem>
              <ListItemText
                primary="Failed to Fetch Stages"
                secondary="Verify the API Key is valid and has access; if you can fetch your stages, then the api key is valid."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Webhook Not Firing"
                secondary="Confirm the webhook is enabled in Leap and points to the exact Paiv URL shown after saving the integration."
              />
            </ListItem>
          </List>
        </Section>
      </Box>
    </Box>
  );
};

export default LeapHelp;
